import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Snackbar,
    SpeedDial,
    SpeedDialAction,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SellIcon from "@mui/icons-material/Sell";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StoreIcon from "@mui/icons-material/Store";

import {
    BrowserRouter as Router,
    Link,
    matchPath,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";

import {
    authenticate,
    fetchUserAPI,
} from "./api/user_api";

import {
    fetchOperatorPrivacyPolicyAPI,
    fetchOperatorTermsAPI,
} from "./api/imprint_api";

import {
    validateNonEmptyAlphanumericString,
    validateNonEmptyEmailAddress,
} from "./api/validation_api";

import {
    reportDSANoticeAPI,
} from "./api/dsa_api";

import {
    handleResponse,
} from "./util/integration";

import {
    PRIMARY_COLOR,
    SECONDARY_COLOR,
} from "./util/constants";

import {
    POLLING_SPEED,
    usePolling,
} from "./util/polling";

import Invoices from "./sections/Invoices";
import Offers from "./sections/Offers";
import Auctions from "./sections/Auctions";
import Profile from "./sections/Profile";
import Purchases from "./sections/Purchases";
import Sales from "./sections/Sales";
import Users from "./sections/Users";

import AuctionWrapper from "./router/AuctionWrapper";
import LeadWrapper from "./router/LeadWrapper";
import OfferWrapper from "./router/OfferWrapper";

import {
    InvoicingWrapper,
} from "./entity/Invoice";

import Imprint from "./legal/Imprint";
import UserImprint from "./legal/UserImprint";

import {
    BACKGROUND_IMAGE_URL,
    LOGO_URL,
    SECONDARY_LOGO_URL,
} from "./util/configuration";
import PaymentSuccess from "./payment/PaymentSuccess";
import PaymentFailed from "./payment/PaymentFailed";
import PaymentOnboardingCompleted from "./payment/PaymentOnboardingCompleted";
import PaymentOnboardingFailed from "./payment/PaymentOnboardingFailed";


const ROUTE = Object.freeze({
    HOME: "/",
    USERS: "/users",
    PURCHASES: "/purchases",
    SALES: "/sales",
    OFFERS: "/offers",
    AUCTIONS: "/auctions",
    INVOICING: "/invoicing",
    INVOICE_DETAILS: "/invoicing/:leadId",
    LEAD_DETAILS: "/leads/:leadId",
    AUCTION_DETAILS: "/auctions/:auctionId",
    OFFER_DETAILS: "/offers/:offerId",
    IMPRINT: "/imprint",
    USER_IMPRINT: "/imprint/:userId",
    PAYMENT_SUCCESS: "/payment_success",
    PAYMENT_FAILED: "/payment_failed",
    PAYMENT_ONBOARDING_SUCCESS: "/payment_onboarding_success",
    PAYMENT_ONBOARDING_FAILED: "/payment_onboarding_failed",
});


const BACKGROUND_ROUTE_PATTERNS =
    Object.freeze([
        ROUTE.IMPRINT,
        ROUTE.USER_IMPRINT,
    ]);


const APP_TEXT = Object.freeze({
    labels: Object.freeze({
        profile: "Profil",
        users: "Benutzer",
        purchases: "Meine Käufe",
        sales: "Meine Verkäufe",
        instantPurchase: "Sofortkauf",
        auction: "Auktion",
        invoicing: "Rechnungen",

        username: "Benutzername",
        password: "Passwort",
        logIn: "Anmelden",
        loggingIn: "Anmeldung läuft …",
        logout: "Abmelden",

        navigation: "Navigation",

        mainLogo: "P-Space Research",
        secondaryLogo: "P-Space Research",

        openHeaderMenu:
            "Kontextmenü öffnen",

        operatorImprint:
            "Impressum",

        operatorTerms:
            "AGB",

        operatorTermsDialogTitle:
            "AGB herunterladen",

        privacyPolicy:
            "Datenschutzerklärung",

        privacyPolicyDialogTitle:
            "Datenschutzerklärung herunterladen",

        dsaReport:
            "Inhalt melden (DSA)",

        dsaDialogTitle:
            "Inhalt melden",

        dsaFirstName:
            "Vorname",

        dsaLastName:
            "Nachname",

        dsaEmailAddress:
            "E-Mail-Adresse",

        dsaType:
            "Art des Inhalts",

        dsaId:
            "ID",

        dsaComment:
            "Grund der Meldung",

        dsaAuction:
            "Auktion",

        dsaOffer:
            "Angebot",
        dsaLead:
            "Lead",
        dsaSend:
            "Meldung absenden",

        cancel:
            "Abbrechen",

        download:
            "Herunterladen",
    }),

    messages: Object.freeze({
        credentialsRequired:
            "Bitte Benutzername und Passwort eingeben.",

        loginSuccessful:
            "Willkommen!",

        loginFailed:
            "Zugriff verweigert.",

        loginRequestFailed:
            "Die Anmeldung konnte nicht durchgeführt werden.",

        contextRefreshFailed:
            "Die Benutzerdaten konnten nicht aktualisiert werden.",

        sessionWarning:
            "Deine Session läuft in zwei Minuten ab. Sie werden in einer Minute abgemeldet..",

        sessionLogout:
            "Sie wurden abgemeldet.",

        operatorTermsConfirmation:
            "Die AGB wurden erfolgreich geladen. Möchten Sie diese jetzt herunterladen?",

        operatorTermsUnavailable:
            "Es wurden noch keine AGB hinterlegt.",

        operatorTermsDownloadFailed:
            "Die AGB konnten nicht geladen werden.",

        privacyPolicyConfirmation:
            "Die Datenschutzerklärung wurde erfolgreich geladen. Möchten Sie diese jetzt herunterladen?",

        privacyPolicyUnavailable:
            "Es wurde noch keine Datenschutzerklärung hinterlegt.",

        privacyPolicyDownloadFailed:
            "Die Datenschutzerklärung konnte nicht geladen werden.",

        dsaDescription:
            "Hier können Sie einen Lead, eine Auktion oder ein Angebot an den Betreiber melden.",

        dsaFirstNameInvalid:
            "Bitte geben Sie einen gültigen Vornamen ein.",

        dsaLastNameInvalid:
            "Bitte geben Sie einen gültigen Nachnamen ein.",

        dsaEmailAddressInvalid:
            "Bitte geben Sie eine gültige E-Mail-Adresse ein.",

        dsaCommentInvalid:
            "Bitte geben Sie einen gültigen Grund für die Meldung ein.",

        dsaTypeInvalid:
            "Bitte wählen Sie Auktion oder Angebot aus.",

        dsaIdInvalid:
            "Bitte geben Sie eine gültige positive ID ein.",

        dsaValidationFailed:
            "Die Eingaben konnten nicht validiert werden.",

        dsaSendSuccessful:
            "Ihre DSA-Meldung wurde erfolgreich an den Betreiber übermittelt.",

        dsaSendFailed:
            "Die DSA-Meldung konnte nicht übermittelt werden.",

        unknownError:
            "Es ist ein unbekannter Fehler aufgetreten.",
    }),
});


const SESSION_CONFIG = Object.freeze({
    warningBeforeExpirationMs:
        2 * 60 * 1000,

    logoutBeforeExpirationMs:
        1 * 60 * 1000,
});


const SNACKBAR_CONFIG = Object.freeze({
    autoHideDurationMs:
        3000,

    zIndex:
        3001,

    emptyKey:
        "empty",

    clickawayReason:
        "clickaway",

    anchorOrigin: Object.freeze({
        vertical: "top",
        horizontal: "center",
    }),
});


const HEADER_CONFIG = Object.freeze({
    heightPx:
        100,

    logoWidthPx:
        120,

    sideColumnWidthPx:
        48,

    borderWidthPx:
        2,

    menuMinWidthPx:
        240,

    appBarZIndex:
        2000,

    menuZIndex:
        2100,

    menuButtonId:
        "header-context-menu-button",

    menuId:
        "header-context-menu",

    ariaExpanded:
        "true",

    loadingIndicatorSizePx:
        20,
});


const HEADER_MENU_POSITION =
    Object.freeze({
        anchorOrigin:
            Object.freeze({
                vertical:
                    "bottom",

                horizontal:
                    "right",
            }),

        transformOrigin:
            Object.freeze({
                vertical:
                    "top",

                horizontal:
                    "right",
            }),
    });


const OPERATOR_TERMS_CONFIG =
    Object.freeze({
        fileName:
            "betreiber-agb.pdf",

        fallbackMimeType:
            "application/pdf",

        revokeObjectUrlDelayMs:
            0,
    });


const PRIVACY_POLICY_CONFIG =
    Object.freeze({
        fileName:
            "datenschutzerklaerung.pdf",

        fallbackMimeType:
            "application/pdf",

        revokeObjectUrlDelayMs:
            0,
    });


const FOOTER_CONFIG =
    Object.freeze({
        heightPx:
            100,

        borderWidthPx:
            2,

        logoMaxWidthPx:
            220,

        logoMaxHeightPx:
            30,

        zIndex:
            2000,
    });


const LAYOUT_CONFIG =
    Object.freeze({
        contentWidth:
            "90%",

        contentPaddingTop:
            3,

        contentPaddingBottom:
            6,

        speedDialBottomPx:
            125,

        speedDialRightPx:
            25,

        speedDialZIndex:
            2000,
    });


const AUTHENTICATION_CONFIG =
    Object.freeze({
        topMargin:
            12.5,

        padding:
            4.5,

        maxWidthPx:
            300,

        borderWidthPx:
            15,

        gridSpacing:
            3,
    });


const NAVIGATION_CONFIG =
    Object.freeze({
        replaceCurrentRoute:
            Object.freeze({
                replace:
                    true,
            }),
    });


const DSA_TYPE =
    Object.freeze({
        AUCTION:
            "AUCTION",

        OFFER:
            "OFFER",
        LEAD:
            "LEAD"
    });


const EMPTY_DSA_ERRORS =
    Object.freeze({
        firstName:
            "",

        lastname:
            "",

        emailAddress:
            "",

        comment:
            "",

        type:
            "",

        id:
            "",
    });


const HEADER_IMPRINT_ITEM =
    Object.freeze({
        label:
            APP_TEXT
                .labels
                .operatorImprint,

        route:
            ROUTE.IMPRINT,

        Icon:
            DescriptionOutlinedIcon,
    });


const HEADER_OPERATOR_TERMS_ITEM =
    Object.freeze({
        label:
            APP_TEXT
                .labels
                .operatorTerms,

        Icon:
            ArticleOutlinedIcon,
    });


const HEADER_PRIVACY_ITEM =
    Object.freeze({
        label:
            APP_TEXT
                .labels
                .privacyPolicy,

        Icon:
            PolicyOutlinedIcon,
    });


const HEADER_DSA_ITEM =
    Object.freeze({
        label:
            APP_TEXT
                .labels
                .dsaReport,

        Icon:
            ReportProblemOutlinedIcon,
    });


const HEADER_LOGOUT_ITEM =
    Object.freeze({
        label:
            APP_TEXT
                .labels
                .logout,

        Icon:
            LogoutIcon,
    });


const STYLE = Object.freeze({
    appShell: {
        minHeight:
            "100vh",

        display:
            "flex",

        flexDirection:
            "column",
    },

    appShellBackground: {
        backgroundImage:
            `url("${BACKGROUND_IMAGE_URL}")`,

        backgroundSize:
            "cover",

        backgroundPosition:
            "center",

        backgroundRepeat:
            "no-repeat",

        backgroundAttachment:
            "fixed",
    },

    routeContainer: {
        width:
            "100%",

        flexGrow:
            1,
    },

    headerSpacer: {
        minHeight:
            `${HEADER_CONFIG.heightPx}px !important`,

        flexShrink:
            0,
    },

    headerAppBar: {
        width:
            "100%",

        backgroundColor:
            PRIMARY_COLOR,

        borderBottom:
            `${HEADER_CONFIG.borderWidthPx}px solid white`,

        zIndex:
            HEADER_CONFIG.appBarZIndex,
    },

    headerToolbar: {
        minHeight:
            `${HEADER_CONFIG.heightPx}px !important`,

        px: {
            xs:
                1.5,

            sm:
                3,
        },

        display:
            "grid",

        gridTemplateColumns:
            `${HEADER_CONFIG.sideColumnWidthPx}px minmax(0, 1fr) ${HEADER_CONFIG.sideColumnWidthPx}px`,

        alignItems:
            "center",
    },

    headerPlaceholder: {
        width:
            HEADER_CONFIG.sideColumnWidthPx,

        height:
            HEADER_CONFIG.sideColumnWidthPx,
    },

    headerLogoLink: {
        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        justifySelf:
            "center",

        textDecoration:
            "none",
    },

    headerLogo: {
        display:
            "block",

        width:
            HEADER_CONFIG.logoWidthPx,

        maxWidth:
            "100%",
    },

    headerMenuButton: {
        justifySelf:
            "end",

        color:
            "white",

        "&:hover": {
            backgroundColor:
                "rgba(255, 255, 255, 0.12)",
        },
    },

    headerMenu: {
        zIndex:
            HEADER_CONFIG.menuZIndex,
    },

    headerMenuPaper: {
        minWidth:
            HEADER_CONFIG.menuMinWidthPx,

        mt:
            1,

        borderRadius:
            2,

        border:
            1,

        borderColor:
            "divider",

        boxShadow:
            "0 8px 30px rgba(15, 23, 42, 0.14)",
    },

    headerMenuItem: {
        gap:
            1.5,

        py:
            1.25,

        color:
            "text.primary",

        textDecoration:
            "none",
    },

    headerMenuItemIcon: {
        color:
            PRIMARY_COLOR,
    },

    headerPrivacyMenuItem: {
        gap:
            1.5,

        py:
            1.25,

        color:
            "text.primary",
    },

    headerPrivacyMenuItemIcon: {
        color:
            PRIMARY_COLOR,
    },

    headerPrivacyProgress: {
        color:
            PRIMARY_COLOR,
    },

    headerMenuDivider: {
        my:
            0.5,
    },

    headerLogoutMenuItem: {
        gap:
            1.5,

        py:
            1.25,

        color:
            "error.main",
    },

    headerLogoutMenuItemIcon: {
        color:
            "error.main",
    },

    privacyDialogPaper: {
        borderRadius:
            2,

        width:
            "100%",

        maxWidth:
            500,
    },

    privacyDialogTitle: {
        color:
            PRIMARY_COLOR,

        fontWeight:
            500,
    },

    privacyDialogCancelButton: {
        color:
            "text.secondary",
    },

    privacyDialogDownloadButton: {
        backgroundColor:
            SECONDARY_COLOR,

        "&:hover": {
            backgroundColor:
                PRIMARY_COLOR,
        },
    },

    dsaDialogPaper: {
        borderRadius:
            2,

        width:
            "100%",

        maxWidth:
            560,
    },

    dsaDialogTitle: {
        color:
            PRIMARY_COLOR,

        fontWeight:
            500,
    },

    dsaDialogContent: {
        display:
            "flex",

        flexDirection:
            "column",

        gap:
            2,

        pt:
            "8px !important",
    },

    dsaDialogDescription: {
        mb:
            1,
    },

    dsaDialogCancelButton: {
        color:
            "text.secondary",
    },

    dsaDialogSubmitButton: {
        backgroundColor:
            SECONDARY_COLOR,

        "&:hover": {
            backgroundColor:
                PRIMARY_COLOR,
        },
    },

    footerSpacer: {
        height:
            FOOTER_CONFIG.heightPx,

        flexShrink:
            0,
    },

    footer: {
        position:
            "fixed",

        left:
            0,

        bottom:
            0,

        width:
            "100%",

        borderTop:
            `${FOOTER_CONFIG.borderWidthPx}px solid white`,

        fontFamily:
            '"Arial", "Helvetica", sans-serif',

        zIndex:
            FOOTER_CONFIG.zIndex,
    },

    footerContent: {
        display:
            "flex",

        justifyContent:
            "center",

        alignItems:
            "center",

        px:
            3,

        py:
            4,

        backgroundColor:
            PRIMARY_COLOR,
    },

    footerLogo: {
        display:
            "block",

        width:
            "100%",

        maxWidth:
            FOOTER_CONFIG.logoMaxWidthPx,

        maxHeight:
            FOOTER_CONFIG.logoMaxHeightPx,

        objectFit:
            "contain",
    },

    applicationMain: {
        display:
            "block",

        width:
            LAYOUT_CONFIG.contentWidth,

        mx:
            "auto",

        pt:
            LAYOUT_CONFIG.contentPaddingTop,

        pb:
            LAYOUT_CONFIG.contentPaddingBottom,
    },

    speedDialContainer: {
        position:
            "fixed",

        bottom:
            LAYOUT_CONFIG.speedDialBottomPx,

        right:
            LAYOUT_CONFIG.speedDialRightPx,

        zIndex:
            LAYOUT_CONFIG.speedDialZIndex,
    },

    speedDialFab: {
        backgroundColor:
            PRIMARY_COLOR,

        "&:hover": {
            backgroundColor:
                SECONDARY_COLOR,
        },
    },

    applicationContent: {
        fontFamily:
            "Arial, Helvetica, sans-serif",
    },

    authenticationPaper: {
        backgroundColor:
            "white",

        mt:
            AUTHENTICATION_CONFIG.topMargin,

        p:
            AUTHENTICATION_CONFIG.padding,

        maxWidth:
            AUTHENTICATION_CONFIG.maxWidthPx,

        border:
            `${AUTHENTICATION_CONFIG.borderWidthPx}px solid ${PRIMARY_COLOR}`,
    },

    authenticationButton: {
        backgroundColor:
            SECONDARY_COLOR,

        "&:hover": {
            backgroundColor:
                PRIMARY_COLOR,
        },
    },

    snackbar: {
        zIndex:
            SNACKBAR_CONFIG.zIndex,

        top: {
            xs:
                16,

            sm:
                24,
        },
    },

    snackbarContent: {
        maxWidth: {
            xs:
                "calc(100vw - 32px)",

            sm:
                600,
        },

        overflowWrap:
            "anywhere",
    },
});


function normalizePathname(
    pathname,
) {
    if (
        typeof pathname !== "string"
        || pathname.trim() === ""
    ) {
        return ROUTE.HOME;
    }

    const pathnameWithoutTrailingSlashes =
        pathname.replace(
            /\/+$/,
            "",
        );

    return (
        pathnameWithoutTrailingSlashes
        || ROUTE.HOME
    );
}


function isBackgroundRoute(
    pathname,
) {
    const normalizedPathname =
        normalizePathname(
            pathname,
        );

    return BACKGROUND_ROUTE_PATTERNS
        .some(
            (routePattern) => (
                matchPath(
                    {
                        path:
                            routePattern,

                        end:
                            true,
                    },

                    normalizedPathname,
                ) !== null
            ),
        );
}


function shouldShowBackgroundImage(
    isLoggedIn,
    pathname,
) {
    if (
        !isLoggedIn
    ) {
        return true;
    }

    return isBackgroundRoute(
        pathname,
    );
}


function createAppShellStyle(
    showBackgroundImage,
) {
    if (
        !showBackgroundImage
    ) {
        return STYLE.appShell;
    }

    return {
        ...STYLE.appShell,
        ...STYLE.appShellBackground,
    };
}


function normalizeMessage(
    message,
) {
    if (
        message instanceof Error
    ) {
        return (
            message.message
            || APP_TEXT
                .messages
                .unknownError
        );
    }

    if (
        typeof message
        === "string"
    ) {
        const normalizedMessage =
            message.trim();

        return (
            normalizedMessage
            || APP_TEXT
                .messages
                .unknownError
        );
    }

    if (
        message === null
        || message === undefined
    ) {
        return (
            APP_TEXT
                .messages
                .unknownError
        );
    }

    try {
        return JSON.stringify(
            message,
        );
    } catch {
        return String(
            message,
        );
    }
}


async function readResponseErrorMessage(
    response,
    fallbackMessage,
) {
    try {
        const responseMessage =
            await response.text();

        const normalizedMessage =
            responseMessage.trim();

        if (
            normalizedMessage
        ) {
            return normalizedMessage;
        }
    } catch {
        /*
         * Die Standardfehlermeldung
         * wird verwendet.
         */
    }

    return normalizeMessage(
        fallbackMessage,
    );
}


function normalizeOperatorTermsBlob(
    responseBlob,
) {
    if (
        responseBlob === null
        || responseBlob === undefined
        || responseBlob.size === 0
    ) {
        throw new Error(
            APP_TEXT
                .messages
                .operatorTermsUnavailable,
        );
    }

    if (
        responseBlob.type
    ) {
        return responseBlob;
    }

    return new Blob(
        [
            responseBlob,
        ],
        {
            type:
                OPERATOR_TERMS_CONFIG
                    .fallbackMimeType,
        },
    );
}


async function requestOperatorTermsBlob() {
    const response =
        await fetchOperatorTermsAPI();

    if (
        response === null
        || response === undefined
    ) {
        throw new Error(
            APP_TEXT
                .messages
                .operatorTermsDownloadFailed,
        );
    }

    if (
        !response.ok
    ) {
        throw new Error(
            await readResponseErrorMessage(
                response,

                APP_TEXT
                    .messages
                    .operatorTermsDownloadFailed,
            ),
        );
    }

    const responseBlob =
        await response.blob();

    return normalizeOperatorTermsBlob(
        responseBlob,
    );
}


function normalizePrivacyPolicyBlob(
    responseBlob,
) {
    if (
        responseBlob === null
        || responseBlob === undefined
        || responseBlob.size === 0
    ) {
        throw new Error(
            APP_TEXT
                .messages
                .privacyPolicyUnavailable,
        );
    }

    if (
        responseBlob.type
    ) {
        return responseBlob;
    }

    return new Blob(
        [
            responseBlob,
        ],
        {
            type:
                PRIVACY_POLICY_CONFIG
                    .fallbackMimeType,
        },
    );
}


async function requestPrivacyPolicyBlob() {
    const response =
        await fetchOperatorPrivacyPolicyAPI();

    if (
        response === null
        || response === undefined
    ) {
        throw new Error(
            APP_TEXT
                .messages
                .privacyPolicyDownloadFailed,
        );
    }

    if (
        !response.ok
    ) {
        throw new Error(
            await readResponseErrorMessage(
                response,

                APP_TEXT
                    .messages
                    .privacyPolicyDownloadFailed,
            ),
        );
    }

    const responseBlob =
        await response.blob();

    return normalizePrivacyPolicyBlob(
        responseBlob,
    );
}


function triggerFileDownload(
    blob,
    fileName,
) {
    const objectUrl =
        window.URL
            .createObjectURL(
                blob,
            );

    const downloadLink =
        document
            .createElement(
                "a",
            );

    downloadLink.href =
        objectUrl;

    downloadLink.download =
        fileName;

    downloadLink.style.display =
        "none";

    document.body
        .appendChild(
            downloadLink,
        );

    downloadLink.click();
    downloadLink.remove();

    window.setTimeout(
        () => {
            window.URL
                .revokeObjectURL(
                    objectUrl,
                );
        },

        PRIVACY_POLICY_CONFIG
            .revokeObjectUrlDelayMs,
    );
}


function useMessageQueue() {
    const [
        messageQueue,
        setMessageQueue,
    ] = useState([]);

    const [
        activeMessage,
        setActiveMessage,
    ] = useState(null);

    const [
        snackbarOpen,
        setSnackbarOpen,
    ] = useState(false);

    const nextMessageIdRef =
        useRef(0);

    const showMessage =
        useCallback(
            (message) => {
                const normalizedMessage =
                    normalizeMessage(
                        message,
                    );

                nextMessageIdRef
                    .current += 1;

                setMessageQueue(
                    (currentQueue) => [
                        ...currentQueue,

                        {
                            id:
                                nextMessageIdRef
                                    .current,

                            text:
                                normalizedMessage,
                        },
                    ],
                );
            },
            [],
        );

    useEffect(
        () => {
            if (
                activeMessage !== null
                || messageQueue.length === 0
            ) {
                return;
            }

            const [
                nextMessage,
                ...remainingMessages
            ] = messageQueue;

            setActiveMessage(
                nextMessage,
            );

            setMessageQueue(
                remainingMessages,
            );

            setSnackbarOpen(
                true,
            );
        },
        [
            activeMessage,
            messageQueue,
        ],
    );

    const closeSnackbar =
        useCallback(
            (
                event,
                reason,
            ) => {
                if (
                    reason
                    === SNACKBAR_CONFIG
                        .clickawayReason
                ) {
                    return;
                }

                setSnackbarOpen(
                    false,
                );
            },
            [],
        );

    const handleSnackbarExited =
        useCallback(
            () => {
                setActiveMessage(
                    null,
                );
            },
            [],
        );

    return {
        activeMessage,
        snackbarOpen,
        showMessage,
        closeSnackbar,
        handleSnackbarExited,
    };
}


function resolveHandledResponse(
    request,
) {
    return new Promise(
        (
            resolve,
            reject,
        ) => {
            handleResponse(
                request,
                resolve,

                (message) => {
                    reject(
                        new Error(
                            normalizeMessage(
                                message,
                            ),
                        ),
                    );
                },
            );
        },
    );
}


function normalizeValidationResult(
    value,
) {
    if (
        typeof value === "boolean"
    ) {
        return value;
    }

    if (
        typeof value === "string"
    ) {
        return (
            value
                .trim()
                .toLowerCase()
            === "true"
        );
    }

    if (
        value !== null
        && typeof value === "object"
    ) {
        if (
            typeof value.valid
            === "boolean"
        ) {
            return value.valid;
        }

        if (
            typeof value.result
            === "boolean"
        ) {
            return value.result;
        }
    }

    return false;
}


function createEmptyDSAErrors() {
    return {
        ...EMPTY_DSA_ERRORS,
    };
}


function normalizeSessionDuration(
    value,
) {
    const duration =
        Number(
            value,
        );

    if (
        !Number.isFinite(
            duration,
        )
        || duration <= 0
    ) {
        return null;
    }

    return duration;
}


function createMenuItems(
    context,
) {
    if (
        context === null
    ) {
        return [];
    }

    const isAdmin =
        Boolean(
            context.admin,
        );

    const isBuyer =
        Boolean(
            context.buyer,
        );

    const isSeller =
        Boolean(
            context.seller,
        );

    return [
        {
            label:
                APP_TEXT
                    .labels
                    .profile,

            to:
                ROUTE.HOME,

            icon:
                <PersonIcon />,
        },

        isAdmin
            ? {
                label:
                    APP_TEXT
                        .labels
                        .users,

                to:
                    ROUTE.USERS,

                icon:
                    <GroupIcon />,
            }
            : null,

        (
            isAdmin
            || isBuyer
        )
            ? {
                label:
                    APP_TEXT
                        .labels
                        .purchases,

                to:
                    ROUTE.PURCHASES,

                icon:
                    <ShoppingCartIcon />,
            }
            : null,

        (
            isAdmin
            || isSeller
        )
            ? {
                label:
                    APP_TEXT
                        .labels
                        .sales,

                to:
                    ROUTE.SALES,

                icon:
                    <SellIcon />,
            }
            : null,

        (
            isAdmin
            || isBuyer
        )
            ? {
                label:
                    APP_TEXT
                        .labels
                        .instantPurchase,

                to:
                    ROUTE.OFFERS,

                icon:
                    <StoreIcon />,
            }
            : null,

        (
            isAdmin
            || isBuyer
        )
            ? {
                label:
                    APP_TEXT
                        .labels
                        .auction,

                to:
                    ROUTE.AUCTIONS,

                icon:
                    <GavelIcon />,
            }
            : null,

        (
            isAdmin
            || isBuyer
            || isSeller
        )
            ? {
                label:
                    APP_TEXT
                        .labels
                        .invoicing,

                to:
                    ROUTE.INVOICING,

                icon:
                    <ReceiptIcon />,
            }
            : null,
    ].filter(
        Boolean,
    );
}


function ApplicationBackground({
    isLoggedIn,
    children,
}) {
    const location =
        useLocation();

    const showBackgroundImage =
        shouldShowBackgroundImage(
            isLoggedIn,
            location.pathname,
        );

    return (
        <Box
            sx={
                createAppShellStyle(
                    showBackgroundImage,
                )
            }
        >
            {children}
        </Box>
    );
}


function App() {
    const [
        context,
        setContext,
    ] = useState(null);

    const [
        contextRefreshVersion,
        setContextRefreshVersion,
    ] = useState(0);

    const contextPollingErrorShownRef =
        useRef(false);

    const [
        username,
        setUsername,
    ] = useState("");

    const [
        password,
        setPassword,
    ] = useState("");

    const [
        accessToken,
        setAccessToken,
    ] = useState(null);

    const [
        sessionTimestamp,
        setSessionTimestamp,
    ] = useState(null);

    const [
        maxSessionDuration,
        setMaxSessionDuration,
    ] = useState(null);

    const [
        isLoggingIn,
        setIsLoggingIn,
    ] = useState(false);

    const {
        activeMessage,
        snackbarOpen,
        showMessage,
        closeSnackbar,
        handleSnackbarExited,
    } = useMessageQueue();

    const requestContextRefresh =
        useCallback(
            () => {
                setContextRefreshVersion(
                    (currentVersion) => (
                        currentVersion + 1
                    ),
                );
            },
            [],
        );

    const logout =
        useCallback(
            () => {
                setContext(
                    null,
                );

                setAccessToken(
                    null,
                );

                setSessionTimestamp(
                    null,
                );

                setMaxSessionDuration(
                    null,
                );

                setPassword(
                    "",
                );

                setContextRefreshVersion(
                    0,
                );

                contextPollingErrorShownRef
                    .current = false;
            },
            [],
        );

    const currentUserId =
        context?.id
        ?? null;

    const contextPollingEnabled = (
        currentUserId !== null
        && currentUserId !== undefined
        && accessToken !== null
        && accessToken !== undefined
    );

    const contextPollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                contextRefreshVersion,
                currentUserId,
            }),
            [
                accessToken,
                contextRefreshVersion,
                currentUserId,
            ],
        );

    const pollCurrentUser =
        useCallback(
            async ({
                isPollingActive,
            }) => {
                if (
                    currentUserId === null
                    || currentUserId === undefined
                    || accessToken === null
                    || accessToken === undefined
                ) {
                    return;
                }

                const refreshedContext =
                    await resolveHandledResponse(
                        fetchUserAPI(
                            currentUserId,
                            accessToken,
                        ),
                    );

                if (
                    !isPollingActive()
                ) {
                    return;
                }

                if (
                    refreshedContext === null
                    || typeof refreshedContext
                    !== "object"
                ) {
                    throw new Error(
                        APP_TEXT
                            .messages
                            .contextRefreshFailed,
                    );
                }

                setContext(
                    (currentContext) => {
                        if (
                            currentContext === null
                            || String(
                                currentContext.id,
                            ) !== String(
                                currentUserId,
                            )
                        ) {
                            return currentContext;
                        }

                        return refreshedContext;
                    },
                );

                contextPollingErrorShownRef
                    .current = false;
            },
            [
                accessToken,
                currentUserId,
            ],
        );

    const handleContextPollingError =
        useCallback(
            () => {
                if (
                    contextPollingErrorShownRef
                        .current
                ) {
                    return;
                }

                contextPollingErrorShownRef
                    .current = true;

                showMessage(
                    APP_TEXT
                        .messages
                        .contextRefreshFailed,
                );
            },
            [
                showMessage,
            ],
        );

    usePolling({
        poll:
            pollCurrentUser,

        enabled:
            contextPollingEnabled,

        immediate:
            true,

        speed:
            POLLING_SPEED.slow,

        onError:
            handleContextPollingError,

        restartKey:
            contextPollingRestartKey,
    });

    useEffect(
        () => {
            if (
                accessToken === null
                || sessionTimestamp === null
                || maxSessionDuration === null
            ) {
                return undefined;
            }

            const sessionExpiresAt =
                sessionTimestamp
                + maxSessionDuration;

            const warningAt =
                sessionExpiresAt
                - SESSION_CONFIG
                    .warningBeforeExpirationMs;

            const logoutAt =
                sessionExpiresAt
                - SESSION_CONFIG
                    .logoutBeforeExpirationMs;

            const now =
                Date.now();

            let warningTimer;
            let logoutTimer;

            const showSessionWarning =
                () => {
                    showMessage(
                        APP_TEXT
                            .messages
                            .sessionWarning,
                    );
                };

            const performSessionLogout =
                () => {
                    logout();

                    showMessage(
                        APP_TEXT
                            .messages
                            .sessionLogout,
                    );
                };

            if (
                logoutAt <= now
            ) {
                performSessionLogout();

                return undefined;
            }

            if (
                warningAt <= now
            ) {
                showSessionWarning();
            } else {
                warningTimer =
                    window.setTimeout(
                        showSessionWarning,
                        warningAt - now,
                    );
            }

            logoutTimer =
                window.setTimeout(
                    performSessionLogout,
                    logoutAt - now,
                );

            return () => {
                if (
                    warningTimer
                    !== undefined
                ) {
                    window.clearTimeout(
                        warningTimer,
                    );
                }

                if (
                    logoutTimer
                    !== undefined
                ) {
                    window.clearTimeout(
                        logoutTimer,
                    );
                }
            };
        },
        [
            accessToken,
            logout,
            maxSessionDuration,
            sessionTimestamp,
            showMessage,
        ],
    );

    const login =
        useCallback(
            async () => {
                const normalizedUsername =
                    username.trim();

                if (
                    !normalizedUsername
                    || !password
                ) {
                    showMessage(
                        APP_TEXT
                            .messages
                            .credentialsRequired,
                    );

                    return;
                }

                setIsLoggingIn(
                    true,
                );

                try {
                    const response =
                        await authenticate(
                            normalizedUsername,
                            password,
                            () => { },
                        );

                    if (
                        response === null
                        || response === undefined
                        || !response.accessToken
                        || !response.user
                    ) {
                        showMessage(
                            APP_TEXT
                                .messages
                                .loginFailed,
                        );

                        return;
                    }

                    setSessionTimestamp(
                        Date.now(),
                    );

                    setMaxSessionDuration(
                        normalizeSessionDuration(
                            response
                                .maxSessionDuration,
                        ),
                    );

                    setAccessToken(
                        response.accessToken,
                    );

                    contextPollingErrorShownRef
                        .current = false;

                    setContext(
                        response.user,
                    );

                    setPassword(
                        "",
                    );

                    showMessage(
                        APP_TEXT
                            .messages
                            .loginSuccessful,
                    );
                } catch {
                    showMessage(
                        APP_TEXT
                            .messages
                            .loginRequestFailed,
                    );
                } finally {
                    setIsLoggingIn(
                        false,
                    );
                }
            },
            [
                password,
                showMessage,
                username,
            ],
        );

    const contextAPI =
        useMemo(
            () => ({
                id:
                    context?.id
                    ?? null,

                username:
                    context?.username
                    ?? null,

                password:
                    accessToken,

                firstName:
                    context?.firstName
                    ?? null,

                lastName:
                    context?.lastName
                    ?? null,

                emailAddress:
                    context?.emailAddress
                    ?? null,

                subscriberBudget:
                    context
                        ?.subscriberBudget
                    ?? null,

                disabled:
                    context?.disabled
                    ?? null,

                seller:
                    context?.seller
                    ?? null,

                buyer:
                    context?.buyer
                    ?? null,

                admin:
                    context?.admin
                    ?? null,

                contract:
                    context?.contract
                    ?? null,

                update:
                    requestContextRefresh,

                logout,
            }),
            [
                accessToken,
                context,
                logout,
                requestContextRefresh,
            ],
        );

    const appAPI =
        useMemo(
            () => ({
                showMessage,
            }),
            [
                showMessage,
            ],
        );

    const isLoggedIn =
        accessToken !== null;

    const sharedContainerProps = {
        context,
        contextAPI,
        appAPI,
        username,
        password,
        setUsername,
        setPassword,
        login,
        isLoggingIn,
    };

    return (
        <Router>
            <ApplicationBackground
                isLoggedIn={
                    isLoggedIn
                }
            >
                <Header
                    isLoggedIn={
                        isLoggedIn
                    }
                    contextAPI={
                        contextAPI
                    }
                    onLogout={
                        logout
                    }
                    showMessage={
                        showMessage
                    }
                />

                <Box
                    sx={
                        STYLE.routeContainer
                    }
                >
                    <Routes>
                        <Route
                            path={
                                ROUTE.HOME
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Profile
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                        userId={
                                            contextAPI.id
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.USERS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Users
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.PURCHASES
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Purchases
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.SALES
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Sales
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.OFFERS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Offers
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.AUCTIONS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Auctions
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.INVOICING
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <Invoices
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE
                                    .INVOICE_DETAILS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <InvoicingWrapper
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE
                                    .LEAD_DETAILS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <LeadWrapper
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE
                                    .AUCTION_DETAILS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <AuctionWrapper
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE
                                    .OFFER_DETAILS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired
                                    showMenu
                                >
                                    <OfferWrapper
                                        appAPI={
                                            appAPI
                                        }
                                        contextAPI={
                                            contextAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE.IMPRINT
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <Imprint
                                        appAPI={
                                            appAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />

                        <Route
                            path={
                                ROUTE
                                    .USER_IMPRINT
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <UserImprint
                                        appAPI={
                                            appAPI
                                        }
                                    />
                                </ApplicationContainer>
                            }
                        />
                        <Route
                            path={
                                ROUTE
                                    .PAYMENT_SUCCESS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <PaymentSuccess />
                                </ApplicationContainer>
                            }
                        />
                        <Route
                            path={
                                ROUTE
                                    .PAYMENT_FAILED
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <PaymentFailed />
                                </ApplicationContainer>
                            }
                        />
                        <Route
                            path={
                                ROUTE
                                    .PAYMENT_ONBOARDING_SUCCESS
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <PaymentOnboardingCompleted />
                                </ApplicationContainer>
                            }
                        />
                        <Route
                            path={
                                ROUTE
                                    .PAYMENT_ONBOARDING_FAILED
                            }
                            element={
                                <ApplicationContainer
                                    {...sharedContainerProps}
                                    authRequired={
                                        false
                                    }
                                    showMenu={
                                        false
                                    }
                                >
                                    <PaymentOnboardingFailed />
                                </ApplicationContainer>
                            }
                        />
                    </Routes>
                </Box>


                <AppSnackbar
                    activeMessage={
                        activeMessage
                    }
                    open={
                        snackbarOpen
                    }
                    onClose={
                        closeSnackbar
                    }
                    onExited={
                        handleSnackbarExited
                    }
                />

                <Footer />
            </ApplicationBackground>
        </Router>
    );
}


function AppSnackbar({
    activeMessage,
    open,
    onClose,
    onExited,
}) {
    return (
        <Snackbar
            key={
                activeMessage?.id
                ?? SNACKBAR_CONFIG
                    .emptyKey
            }
            open={
                open
            }
            autoHideDuration={
                SNACKBAR_CONFIG
                    .autoHideDurationMs
            }
            anchorOrigin={
                SNACKBAR_CONFIG
                    .anchorOrigin
            }
            onClose={
                onClose
            }
            message={
                activeMessage?.text
                ?? ""
            }
            TransitionProps={{
                onExited,
            }}
            ContentProps={{
                sx:
                    STYLE
                        .snackbarContent,
            }}
            sx={
                STYLE.snackbar
            }
        />
    );
}


function Header({
    isLoggedIn,
    contextAPI,
    onLogout,
    showMessage,
}) {
    const [
        menuAnchor,
        setMenuAnchor,
    ] = useState(null);

    const [
        isLoadingOperatorTerms,
        setIsLoadingOperatorTerms,
    ] = useState(false);

    const [
        operatorTermsBlob,
        setOperatorTermsBlob,
    ] = useState(null);

    const [
        operatorTermsDialogOpen,
        setOperatorTermsDialogOpen,
    ] = useState(false);

    const [
        isLoadingPrivacyPolicy,
        setIsLoadingPrivacyPolicy,
    ] = useState(false);

    const [
        privacyPolicyBlob,
        setPrivacyPolicyBlob,
    ] = useState(null);

    const [
        privacyDialogOpen,
        setPrivacyDialogOpen,
    ] = useState(false);

    const [
        dsaDialogOpen,
        setDSADialogOpen,
    ] = useState(false);

    const [
        isSubmittingDSA,
        setIsSubmittingDSA,
    ] = useState(false);

    const [
        dsaNotice,
        setDSANotice,
    ] = useState({
        firstName:
            "",

        lastname:
            "",

        emailAddress:
            "",

        comment:
            "",

        type:
            "",

        id:
            "",
    });

    const [
        dsaErrors,
        setDSAErrors,
    ] = useState(
        createEmptyDSAErrors,
    );

    const navigate =
        useNavigate();

    const menuIsOpen =
        Boolean(
            menuAnchor,
        );


    const openMenu = (
        event,
    ) => {
        setMenuAnchor(
            event.currentTarget,
        );
    };


    const closeMenu =
        () => {
            setMenuAnchor(
                null,
            );
        };


    const closeOperatorTermsDialog =
        () => {
            setOperatorTermsDialogOpen(
                false,
            );

            setOperatorTermsBlob(
                null,
            );
        };


    const closePrivacyDialog =
        () => {
            setPrivacyDialogOpen(
                false,
            );

            setPrivacyPolicyBlob(
                null,
            );
        };


    const openDSADialog =
        () => {
            closeMenu();

            setDSANotice({
                firstName:
                    contextAPI
                        ?.firstName
                        ?.trim()
                    ?? "",

                lastname:
                    contextAPI
                        ?.lastName
                        ?.trim()
                    ?? "",

                emailAddress:
                    contextAPI
                        ?.emailAddress
                        ?.trim()
                    ?? "",

                comment:
                    "",

                type:
                    "",

                id:
                    "",
            });

            setDSAErrors(
                createEmptyDSAErrors(),
            );

            setDSADialogOpen(
                true,
            );
        };


    const closeDSADialog =
        () => {
            if (
                isSubmittingDSA
            ) {
                return;
            }

            setDSADialogOpen(
                false,
            );

            setDSAErrors(
                createEmptyDSAErrors(),
            );
        };


    const updateDSAField =
        (field) => (
            event,
        ) => {
            const value =
                event
                    .target
                    .value;

            setDSANotice(
                (currentNotice) => ({
                    ...currentNotice,

                    [field]:
                        value,
                }),
            );

            setDSAErrors(
                (currentErrors) => ({
                    ...currentErrors,

                    [field]:
                        "",
                }),
            );
        };


    const validateDSANotice =
        async () => {
            const normalizedNotice = {
                firstName:
                    dsaNotice
                        .firstName
                        .trim(),

                lastname:
                    dsaNotice
                        .lastname
                        .trim(),

                emailAddress:
                    dsaNotice
                        .emailAddress
                        .trim(),

                comment:
                    dsaNotice
                        .comment
                        .trim(),

                type:
                    dsaNotice.type,

                id:
                    String(
                        dsaNotice.id,
                    ).trim(),
            };

            let validationResults;

            try {
                validationResults =
                    await Promise.all([
                        resolveHandledResponse(
                            validateNonEmptyAlphanumericString(
                                normalizedNotice
                                    .firstName,
                            ),
                        ),

                        resolveHandledResponse(
                            validateNonEmptyAlphanumericString(
                                normalizedNotice
                                    .lastname,
                            ),
                        ),

                        resolveHandledResponse(
                            validateNonEmptyEmailAddress(
                                normalizedNotice
                                    .emailAddress,
                            ),
                        ),

                        resolveHandledResponse(
                            validateNonEmptyAlphanumericString(
                                normalizedNotice
                                    .comment,
                            ),
                        ),
                    ]);
            } catch (error) {
                throw new Error(
                    normalizeMessage(
                        error
                        ?? APP_TEXT
                            .messages
                            .dsaValidationFailed,
                    ),
                );
            }

            const [
                firstNameResult,
                lastNameResult,
                emailAddressResult,
                commentResult,
            ] = validationResults;

            const idNumber =
                Number(
                    normalizedNotice.id,
                );

            const errors =
                createEmptyDSAErrors();

            if (
                !normalizeValidationResult(
                    firstNameResult,
                )
            ) {
                errors.firstName =
                    APP_TEXT
                        .messages
                        .dsaFirstNameInvalid;
            }

            if (
                !normalizeValidationResult(
                    lastNameResult,
                )
            ) {
                errors.lastname =
                    APP_TEXT
                        .messages
                        .dsaLastNameInvalid;
            }

            if (
                !normalizeValidationResult(
                    emailAddressResult,
                )
            ) {
                errors.emailAddress =
                    APP_TEXT
                        .messages
                        .dsaEmailAddressInvalid;
            }

            if (
                !normalizeValidationResult(
                    commentResult,
                )
            ) {
                errors.comment =
                    APP_TEXT
                        .messages
                        .dsaCommentInvalid;
            }

            if (
                normalizedNotice.type
                !== DSA_TYPE.AUCTION
                && normalizedNotice.type
                !== DSA_TYPE.OFFER
            ) {
                errors.type =
                    APP_TEXT
                        .messages
                        .dsaTypeInvalid;
            }

            if (
                !/^[1-9][0-9]*$/
                    .test(
                        normalizedNotice.id,
                    )
                || !Number.isSafeInteger(
                    idNumber,
                )
            ) {
                errors.id =
                    APP_TEXT
                        .messages
                        .dsaIdInvalid;
            }

            setDSAErrors(
                errors,
            );

            const hasErrors =
                Object
                    .values(
                        errors,
                    )
                    .some(
                        Boolean,
                    );

            if (
                hasErrors
            ) {
                return null;
            }

            return {
                ...normalizedNotice,

                id:
                    idNumber,
            };
        };


    const handleDSASubmit =
        async (
            event,
        ) => {
            event.preventDefault();

            if (
                isSubmittingDSA
            ) {
                return;
            }

            setIsSubmittingDSA(
                true,
            );

            try {
                const validatedNotice =
                    await validateDSANotice();

                if (
                    validatedNotice === null
                ) {
                    return;
                }

                const response =
                    await reportDSANoticeAPI(
                        validatedNotice
                            .firstName,

                        validatedNotice
                            .lastname,

                        validatedNotice
                            .emailAddress,

                        validatedNotice
                            .comment,

                        validatedNotice
                            .type,

                        validatedNotice
                            .id,
                    );

                if (
                    response === null
                    || response === undefined
                    || !response.ok
                ) {
                    throw new Error(
                        response
                            ? await readResponseErrorMessage(
                                response,

                                APP_TEXT
                                    .messages
                                    .dsaSendFailed,
                            )
                            : APP_TEXT
                                .messages
                                .dsaSendFailed,
                    );
                }

                setDSADialogOpen(
                    false,
                );

                setDSAErrors(
                    createEmptyDSAErrors(),
                );

                showMessage?.(
                    APP_TEXT
                        .messages
                        .dsaSendSuccessful,
                );
            } catch (error) {
                showMessage?.(
                    error
                    ?? APP_TEXT
                        .messages
                        .dsaSendFailed,
                );
            } finally {
                setIsSubmittingDSA(
                    false,
                );
            }
        };


    const handleLogout =
        () => {
            closeMenu();

            onLogout?.();

            navigate(
                ROUTE.HOME,

                NAVIGATION_CONFIG
                    .replaceCurrentRoute,
            );
        };


    const handleOperatorTermsRequest =
        async () => {
            if (
                isLoadingOperatorTerms
            ) {
                return;
            }

            closeMenu();

            setIsLoadingOperatorTerms(
                true,
            );

            try {
                const loadedOperatorTermsBlob =
                    await requestOperatorTermsBlob();

                setOperatorTermsBlob(
                    loadedOperatorTermsBlob,
                );

                setOperatorTermsDialogOpen(
                    true,
                );
            } catch (error) {
                showMessage?.(
                    error
                    ?? APP_TEXT
                        .messages
                        .operatorTermsDownloadFailed,
                );
            } finally {
                setIsLoadingOperatorTerms(
                    false,
                );
            }
        };


    const handleOperatorTermsDownload =
        () => {
            if (
                operatorTermsBlob === null
                || operatorTermsBlob === undefined
                || operatorTermsBlob.size === 0
            ) {
                closeOperatorTermsDialog();

                showMessage?.(
                    APP_TEXT
                        .messages
                        .operatorTermsUnavailable,
                );

                return;
            }

            triggerFileDownload(
                operatorTermsBlob,

                OPERATOR_TERMS_CONFIG
                    .fileName,
            );

            closeOperatorTermsDialog();
        };


    const handlePrivacyPolicyRequest =
        async () => {
            if (
                isLoadingPrivacyPolicy
            ) {
                return;
            }

            closeMenu();

            setIsLoadingPrivacyPolicy(
                true,
            );

            try {
                const loadedPrivacyPolicyBlob =
                    await requestPrivacyPolicyBlob();

                setPrivacyPolicyBlob(
                    loadedPrivacyPolicyBlob,
                );

                setPrivacyDialogOpen(
                    true,
                );
            } catch (error) {
                showMessage?.(
                    error
                    ?? APP_TEXT
                        .messages
                        .privacyPolicyDownloadFailed,
                );
            } finally {
                setIsLoadingPrivacyPolicy(
                    false,
                );
            }
        };


    const handlePrivacyPolicyDownload =
        () => {
            if (
                privacyPolicyBlob === null
                || privacyPolicyBlob === undefined
                || privacyPolicyBlob.size === 0
            ) {
                closePrivacyDialog();

                showMessage?.(
                    APP_TEXT
                        .messages
                        .privacyPolicyUnavailable,
                );

                return;
            }

            triggerFileDownload(
                privacyPolicyBlob,

                PRIVACY_POLICY_CONFIG
                    .fileName,
            );

            closePrivacyDialog();
        };


    const ImprintItemIcon =
        HEADER_IMPRINT_ITEM.Icon;

    const OperatorTermsItemIcon =
        HEADER_OPERATOR_TERMS_ITEM.Icon;

    const PrivacyItemIcon =
        HEADER_PRIVACY_ITEM.Icon;

    const DSAItemIcon =
        HEADER_DSA_ITEM.Icon;

    const LogoutItemIcon =
        HEADER_LOGOUT_ITEM.Icon;


    return (
        <>
            <Toolbar
                sx={
                    STYLE.headerSpacer
                }
            />

            <AppBar
                component="header"
                position="fixed"
                elevation={0}
                sx={
                    STYLE.headerAppBar
                }
            >
                <Toolbar
                    sx={
                        STYLE
                            .headerToolbar
                    }
                >
                    <Box
                        aria-hidden="true"
                        sx={
                            STYLE
                                .headerPlaceholder
                        }
                    />

                    <Box
                        component={
                            Link
                        }
                        to={
                            ROUTE.HOME
                        }
                        aria-label={
                            APP_TEXT
                                .labels
                                .mainLogo
                        }
                        sx={
                            STYLE
                                .headerLogoLink
                        }
                    >
                        <Box
                            component="img"
                            src={
                                LOGO_URL
                            }
                            alt={
                                APP_TEXT
                                    .labels
                                    .mainLogo
                            }
                            sx={
                                STYLE.headerLogo
                            }
                        />
                    </Box>

                    <IconButton
                        id={
                            HEADER_CONFIG
                                .menuButtonId
                        }
                        aria-label={
                            APP_TEXT
                                .labels
                                .openHeaderMenu
                        }
                        aria-controls={
                            menuIsOpen
                                ? HEADER_CONFIG
                                    .menuId
                                : undefined
                        }
                        aria-haspopup="true"
                        aria-expanded={
                            menuIsOpen
                                ? HEADER_CONFIG
                                    .ariaExpanded
                                : undefined
                        }
                        onClick={
                            openMenu
                        }
                        sx={
                            STYLE
                                .headerMenuButton
                        }
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Menu
                id={
                    HEADER_CONFIG.menuId
                }
                anchorEl={
                    menuAnchor
                }
                open={
                    menuIsOpen
                }
                onClose={
                    closeMenu
                }
                MenuListProps={{
                    "aria-labelledby":
                        HEADER_CONFIG
                            .menuButtonId,
                }}
                anchorOrigin={
                    HEADER_MENU_POSITION
                        .anchorOrigin
                }
                transformOrigin={
                    HEADER_MENU_POSITION
                        .transformOrigin
                }
                PaperProps={{
                    sx:
                        STYLE
                            .headerMenuPaper,
                }}
                sx={
                    STYLE.headerMenu
                }
            >
                <MenuItem
                    component={
                        Link
                    }
                    to={
                        HEADER_IMPRINT_ITEM
                            .route
                    }
                    onClick={
                        closeMenu
                    }
                    sx={
                        STYLE.headerMenuItem
                    }
                >
                    <ImprintItemIcon
                        fontSize="small"
                        sx={
                            STYLE
                                .headerMenuItemIcon
                        }
                    />

                    <Typography
                        component="span"
                        variant="body1"
                    >
                        {
                            HEADER_IMPRINT_ITEM
                                .label
                        }
                    </Typography>
                </MenuItem>

                <MenuItem
                    onClick={
                        handleOperatorTermsRequest
                    }
                    disabled={
                        isLoadingOperatorTerms
                    }
                    sx={
                        STYLE
                            .headerPrivacyMenuItem
                    }
                >
                    {
                        isLoadingOperatorTerms
                            ? (
                                <CircularProgress
                                    size={
                                        HEADER_CONFIG
                                            .loadingIndicatorSizePx
                                    }
                                    sx={
                                        STYLE
                                            .headerPrivacyProgress
                                    }
                                />
                            )
                            : (
                                <OperatorTermsItemIcon
                                    fontSize="small"
                                    sx={
                                        STYLE
                                            .headerPrivacyMenuItemIcon
                                    }
                                />
                            )
                    }

                    <Typography
                        component="span"
                        variant="body1"
                    >
                        {
                            HEADER_OPERATOR_TERMS_ITEM
                                .label
                        }
                    </Typography>
                </MenuItem>

                <MenuItem
                    onClick={
                        handlePrivacyPolicyRequest
                    }
                    disabled={
                        isLoadingPrivacyPolicy
                    }
                    sx={
                        STYLE
                            .headerPrivacyMenuItem
                    }
                >
                    {
                        isLoadingPrivacyPolicy
                            ? (
                                <CircularProgress
                                    size={
                                        HEADER_CONFIG
                                            .loadingIndicatorSizePx
                                    }
                                    sx={
                                        STYLE
                                            .headerPrivacyProgress
                                    }
                                />
                            )
                            : (
                                <PrivacyItemIcon
                                    fontSize="small"
                                    sx={
                                        STYLE
                                            .headerPrivacyMenuItemIcon
                                    }
                                />
                            )
                    }

                    <Typography
                        component="span"
                        variant="body1"
                    >
                        {
                            HEADER_PRIVACY_ITEM
                                .label
                        }
                    </Typography>
                </MenuItem>

                <MenuItem
                    onClick={
                        openDSADialog
                    }
                    sx={
                        STYLE
                            .headerPrivacyMenuItem
                    }
                >
                    <DSAItemIcon
                        fontSize="small"
                        sx={
                            STYLE
                                .headerPrivacyMenuItemIcon
                        }
                    />

                    <Typography
                        component="span"
                        variant="body1"
                    >
                        {
                            HEADER_DSA_ITEM
                                .label
                        }
                    </Typography>
                </MenuItem>

                {
                    isLoggedIn
                    && (
                        <>
                            <Divider
                                sx={
                                    STYLE
                                        .headerMenuDivider
                                }
                            />

                            <MenuItem
                                onClick={
                                    handleLogout
                                }
                                sx={
                                    STYLE
                                        .headerLogoutMenuItem
                                }
                            >
                                <LogoutItemIcon
                                    fontSize="small"
                                    sx={
                                        STYLE
                                            .headerLogoutMenuItemIcon
                                    }
                                />

                                <Typography
                                    component="span"
                                    variant="body1"
                                >
                                    {
                                        HEADER_LOGOUT_ITEM
                                            .label
                                    }
                                </Typography>
                            </MenuItem>
                        </>
                    )
                }
            </Menu>

            <Dialog
                open={
                    operatorTermsDialogOpen
                }
                onClose={
                    closeOperatorTermsDialog
                }
                aria-labelledby={
                    "operator-terms-dialog-title"
                }
                PaperProps={{
                    sx:
                        STYLE
                            .privacyDialogPaper,
                }}
            >
                <DialogTitle
                    id={
                        "operator-terms-dialog-title"
                    }
                    sx={
                        STYLE
                            .privacyDialogTitle
                    }
                >
                    {
                        APP_TEXT
                            .labels
                            .operatorTermsDialogTitle
                    }
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {
                            APP_TEXT
                                .messages
                                .operatorTermsConfirmation
                        }
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        type="button"
                        onClick={
                            closeOperatorTermsDialog
                        }
                        sx={
                            STYLE
                                .privacyDialogCancelButton
                        }
                    >
                        {
                            APP_TEXT
                                .labels
                                .cancel
                        }
                    </Button>

                    <Button
                        type="button"
                        variant="contained"
                        onClick={
                            handleOperatorTermsDownload
                        }
                        sx={
                            STYLE
                                .privacyDialogDownloadButton
                        }
                    >
                        {
                            APP_TEXT
                                .labels
                                .download
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={
                    privacyDialogOpen
                }
                onClose={
                    closePrivacyDialog
                }
                aria-labelledby={
                    "privacy-policy-dialog-title"
                }
                PaperProps={{
                    sx:
                        STYLE
                            .privacyDialogPaper,
                }}
            >
                <DialogTitle
                    id={
                        "privacy-policy-dialog-title"
                    }
                    sx={
                        STYLE
                            .privacyDialogTitle
                    }
                >
                    {
                        APP_TEXT
                            .labels
                            .privacyPolicyDialogTitle
                    }
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {
                            APP_TEXT
                                .messages
                                .privacyPolicyConfirmation
                        }
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        type="button"
                        onClick={
                            closePrivacyDialog
                        }
                        sx={
                            STYLE
                                .privacyDialogCancelButton
                        }
                    >
                        {
                            APP_TEXT
                                .labels
                                .cancel
                        }
                    </Button>

                    <Button
                        type="button"
                        variant="contained"
                        onClick={
                            handlePrivacyPolicyDownload
                        }
                        sx={
                            STYLE
                                .privacyDialogDownloadButton
                        }
                    >
                        {
                            APP_TEXT
                                .labels
                                .download
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={
                    dsaDialogOpen
                }
                onClose={
                    closeDSADialog
                }
                aria-labelledby={
                    "dsa-dialog-title"
                }
                PaperProps={{
                    sx:
                        STYLE
                            .dsaDialogPaper,
                }}
            >
                <Box
                    component="form"
                    onSubmit={
                        handleDSASubmit
                    }
                    noValidate
                >
                    <DialogTitle
                        id={
                            "dsa-dialog-title"
                        }
                        sx={
                            STYLE
                                .dsaDialogTitle
                        }
                    >
                        {
                            APP_TEXT
                                .labels
                                .dsaDialogTitle
                        }
                    </DialogTitle>

                    <DialogContent
                        sx={
                            STYLE
                                .dsaDialogContent
                        }
                    >
                        <DialogContentText
                            sx={
                                STYLE
                                    .dsaDialogDescription
                            }
                        >
                            {
                                APP_TEXT
                                    .messages
                                    .dsaDescription
                            }
                        </DialogContentText>

                        <TextField
                            value={
                                dsaNotice.firstName
                            }
                            onChange={
                                updateDSAField(
                                    "firstName",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaFirstName
                            }
                            error={
                                Boolean(
                                    dsaErrors.firstName,
                                )
                            }
                            helperText={
                                dsaErrors.firstName
                            }
                            autoComplete="given-name"
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        />

                        <TextField
                            value={
                                dsaNotice.lastname
                            }
                            onChange={
                                updateDSAField(
                                    "lastname",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaLastName
                            }
                            error={
                                Boolean(
                                    dsaErrors.lastname,
                                )
                            }
                            helperText={
                                dsaErrors.lastname
                            }
                            autoComplete="family-name"
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        />

                        <TextField
                            value={
                                dsaNotice.emailAddress
                            }
                            onChange={
                                updateDSAField(
                                    "emailAddress",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaEmailAddress
                            }
                            error={
                                Boolean(
                                    dsaErrors.emailAddress,
                                )
                            }
                            helperText={
                                dsaErrors.emailAddress
                            }
                            type="email"
                            autoComplete="email"
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        />

                        <TextField
                            select
                            value={
                                dsaNotice.type
                            }
                            onChange={
                                updateDSAField(
                                    "type",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaType
                            }
                            error={
                                Boolean(
                                    dsaErrors.type,
                                )
                            }
                            helperText={
                                dsaErrors.type
                            }
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        >
                            <MenuItem
                                value={
                                    DSA_TYPE.AUCTION
                                }
                            >
                                {
                                    APP_TEXT
                                        .labels
                                        .dsaAuction
                                }
                            </MenuItem>

                            <MenuItem
                                value={
                                    DSA_TYPE.OFFER
                                }
                            >
                                {
                                    APP_TEXT
                                        .labels
                                        .dsaOffer
                                }
                            </MenuItem>
                            <MenuItem
                                value={
                                    DSA_TYPE.LEAD
                                }
                            >
                                {
                                    APP_TEXT
                                        .labels
                                        .dsaLead
                                }
                            </MenuItem>
                        </TextField>

                        <TextField
                            value={
                                dsaNotice.id
                            }
                            onChange={
                                updateDSAField(
                                    "id",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaId
                            }
                            error={
                                Boolean(
                                    dsaErrors.id,
                                )
                            }
                            helperText={
                                dsaErrors.id
                            }
                            type="number"
                            inputProps={{
                                min:
                                    1,

                                step:
                                    1,
                            }}
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        />

                        <TextField
                            value={
                                dsaNotice.comment
                            }
                            onChange={
                                updateDSAField(
                                    "comment",
                                )
                            }
                            label={
                                APP_TEXT
                                    .labels
                                    .dsaComment
                            }
                            error={
                                Boolean(
                                    dsaErrors.comment,
                                )
                            }
                            helperText={
                                dsaErrors.comment
                            }
                            multiline
                            minRows={4}
                            maxRows={10}
                            disabled={
                                isSubmittingDSA
                            }
                            required
                            fullWidth
                        />
                    </DialogContent>

                    <DialogActions>
                        <Button
                            type="button"
                            onClick={
                                closeDSADialog
                            }
                            disabled={
                                isSubmittingDSA
                            }
                            sx={
                                STYLE
                                    .dsaDialogCancelButton
                            }
                        >
                            {
                                APP_TEXT
                                    .labels
                                    .cancel
                            }
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={
                                isSubmittingDSA
                            }
                            sx={
                                STYLE
                                    .dsaDialogSubmitButton
                            }
                        >
                            {
                                isSubmittingDSA
                                    ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    )
                                    : APP_TEXT
                                        .labels
                                        .dsaSend
                            }
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
}


function Footer() {
    return (
        <>
            <Box
                sx={
                    STYLE.footerSpacer
                }
            />

            <Box
                component="footer"
                sx={
                    STYLE.footer
                }
            >
                <Box
                    sx={
                        STYLE.footerContent
                    }
                >
                    <Box
                        component="img"
                        src={
                            SECONDARY_LOGO_URL
                        }
                        alt={
                            APP_TEXT
                                .labels
                                .secondaryLogo
                        }
                        sx={
                            STYLE.footerLogo
                        }
                    />
                </Box>
            </Box>
        </>
    );
}


function ApplicationContainer({
    authRequired,
    context,
    username,
    password,
    setUsername,
    setPassword,
    login,
    isLoggingIn,
    showMenu,
    children,
}) {
    const menuItems =
        useMemo(
            () => (
                createMenuItems(
                    context,
                )
            ),
            [
                context,
            ],
        );

    const canShowContent = (
        context !== null
        || authRequired === false
    );

    return (
        <Box
            component="main"
            sx={
                STYLE.applicationMain
            }
        >
            {
                canShowContent
                    ? (
                        <>
                            {
                                showMenu
                                && (
                                    <Box
                                        sx={
                                            STYLE
                                                .speedDialContainer
                                        }
                                    >
                                        <SpeedDial
                                            ariaLabel={
                                                APP_TEXT
                                                    .labels
                                                    .navigation
                                            }
                                            icon={
                                                <MenuIcon />
                                            }
                                            FabProps={{
                                                sx:
                                                    STYLE
                                                        .speedDialFab,
                                            }}
                                        >
                                            {
                                                menuItems.map(
                                                    (item) => (
                                                        <SpeedDialAction
                                                            key={
                                                                item.to
                                                            }
                                                            icon={
                                                                item.icon
                                                            }
                                                            tooltipTitle={
                                                                item.label
                                                            }
                                                            FabProps={{
                                                                component:
                                                                    Link,

                                                                to:
                                                                    item.to,
                                                            }}
                                                        />
                                                    ),
                                                )
                                            }
                                        </SpeedDial>
                                    </Box>
                                )
                            }

                            <Box
                                sx={
                                    STYLE
                                        .applicationContent
                                }
                            >
                                {children}
                            </Box>
                        </>
                    )
                    : (
                        <Authentication
                            username={
                                username
                            }
                            password={
                                password
                            }
                            setUsername={
                                setUsername
                            }
                            setPassword={
                                setPassword
                            }
                            login={
                                login
                            }
                            isLoggingIn={
                                isLoggingIn
                            }
                        />
                    )
            }
        </Box>
    );
}


function Authentication({
    username,
    password,
    setUsername,
    setPassword,
    login,
    isLoggingIn,
}) {
    const handleSubmit = (
        event,
    ) => {
        event.preventDefault();

        login();
    };

    return (
        <Paper
            component="form"
            onSubmit={
                handleSubmit
            }
            sx={
                STYLE
                    .authenticationPaper
            }
        >
            <Grid
                container
                spacing={
                    AUTHENTICATION_CONFIG
                        .gridSpacing
                }
            >
                <Grid
                    item
                    xs={12}
                >
                    <TextField
                        value={
                            username
                        }
                        onChange={(
                            event,
                        ) => {
                            setUsername(
                                event
                                    .target
                                    .value,
                            );
                        }}
                        label={
                            APP_TEXT
                                .labels
                                .username
                        }
                        variant="outlined"
                        autoComplete="username"
                        disabled={
                            isLoggingIn
                        }
                        fullWidth
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                >
                    <TextField
                        value={
                            password
                        }
                        onChange={(
                            event,
                        ) => {
                            setPassword(
                                event
                                    .target
                                    .value,
                            );
                        }}
                        label={
                            APP_TEXT
                                .labels
                                .password
                        }
                        variant="outlined"
                        type="password"
                        autoComplete="current-password"
                        disabled={
                            isLoggingIn
                        }
                        fullWidth
                    />
                </Grid>

                <Grid
                    item
                    xs={12}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            isLoggingIn
                        }
                        sx={
                            STYLE
                                .authenticationButton
                        }
                    >
                        {
                            isLoggingIn
                                ? APP_TEXT
                                    .labels
                                    .loggingIn
                                : APP_TEXT
                                    .labels
                                    .logIn
                        }
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
}


export default App;